import { FC, useRef, useState, useEffect } from 'react'
import classNames from 'classnames'
import { useTranslation } from 'next-i18next'
import { HorizontalBar, defaults } from 'react-chartjs-2'
import parse from 'html-react-parser'
import ButtonGroup from 'components/button-group/ButtonGroup'
import { BarChartOptions, EnergyType, RegionType } from './types'
import styles from './Charts.module.scss'

defaults.global.defaultFontFamily = '"commuters-sanscommuterssansRg", sans-serif'

const BarChartMobile: FC = () => {
  /**
   * Chart data
   *
   * For some reason the data variables need to be defined inside the component
   * Importing them from an external file messes with chart update behaviour :/
   */
  const C2 = '#8C98C5' // color 'big 4'
  const C3 = '#23D5AE' // color Bolt
  const ELECTRICITY_FLA = {
    labels: ['Eneco', 'Luminus', 'Engie Electrabel', 'Lampiris', 'Bolt'],
    datasets: [
      {
        data: [1091, 1084, 1050, 1044, 924],
        backgroundColor: [C2, C2, C2, C2, C3],
        barThickness: 30
      }
    ]
  }
  const ELECTRICITY_WAL = {
    labels: ['Luminus', 'Eneco', 'Engie Electrabel', 'Lampiris', 'Bolt'],
    datasets: [
      {
        data: [1125, 1115, 1077, 1060, 939],
        backgroundColor: [C2, C2, C2, C2, C3],
        barThickness: 30
      }
    ]
  }
  const GAS_FLA = {
    labels: ['Eneco', 'Luminus', 'Engie Electrabel', 'Lampiris', 'Bolt'],
    datasets: [
      {
        data: [997, 992, 953, 931, 711],
        backgroundColor: [C2, C2, C2, C2, C3],
        barThickness: 30
      }
    ]
  }
  const GAS_WAL = {
    labels: ['Eneco', 'Luminus', 'Engie Electrabel', 'Lampiris', 'Bolt'],
    datasets: [
      {
        data: [1279, 1253, 1243, 1182, 975],
        backgroundColor: [C2, C2, C2, C2, C3],
        barThickness: 30
      }
    ]
  }

  // I18N
  const { t } = useTranslation('pricing')

  // Ref
  const chartRef = useRef(null)

  // State
  const [chartData, setChartData] = useState(ELECTRICITY_FLA)
  const [energyType, setEnergyType] = useState<EnergyType>(EnergyType.ELECTRICITY)
  const [region, setRegion] = useState<RegionType>(RegionType.FLA)

  // Chart configuration
  const options: BarChartOptions = {
    maintainAspectRatio: false,
    legend: {
      display: false
    },
    tooltips: {
      xPadding: 10,
      yPadding: 10,
      cornerRadius: 12,
      yAlign: 'top',
      xAlign: 'center',
      bodyAlign: 'center',
      displayColors: false,
      caretPadding: 10,
      callbacks: {
        label: (tooltipItem) => {
          return `€ ${tooltipItem.value}`
        }
      }
    },
    scales: {
      xAxes: [
        {
          beforeFit: (scale) => {
            // find max value in dataset
            let maxValue = 0
            if (scale.chart.config.data.datasets) {
              scale.chart.config.data.datasets.forEach((dataset: any) => {
                if (dataset.data) {
                  dataset.data.forEach((value: any) => {
                    if (value > maxValue) {
                      maxValue = value
                    }
                  })
                }
              })
            }
            // round maxValue to hundreds and set value
            scale.options.ticks.max = Math.ceil(maxValue / 100) * 100
          },
          ticks: {
            min: 500,
            stepSize: 100,
            maxTicksLimit: 8,
            callback: (value: string) => {
              return `€ ${value}`
            }
          },
          gridLines: {
            drawBorder: false
          }
        }
      ],
      yAxes: [
        {
          gridLines: {
            drawBorder: false,
            display: false
          }
        }
      ]
    }
  }

  /**
   * Updates chart with new data
   * @param chart
   * @param newData
   */
  const updateData = (chart: any, newData: any) => {
    chart.data.labels = newData.labels
    chart.data.datasets[0].data = newData.datasets[0].data
    chart.data.datasets[0].backgroundColor = newData.datasets[0].backgroundColor
    chart.update()
    setChartData(newData)
  }

  /**
   * Triggers chart update
   */
  useEffect(() => {
    const chart = chartRef?.current?.chartInstance

    if (energyType === EnergyType.ELECTRICITY && region === RegionType.FLA) {
      updateData(chart, ELECTRICITY_FLA)
    }
    if (energyType === EnergyType.ELECTRICITY && region === RegionType.WAL) {
      updateData(chart, ELECTRICITY_WAL)
    }
    if (energyType === EnergyType.GAS && region === RegionType.FLA) {
      updateData(chart, GAS_FLA)
    }
    if (energyType === EnergyType.GAS && region === RegionType.WAL) {
      updateData(chart, GAS_WAL)
    }
  }, [energyType, region])

  return (
    <div className={styles['bar-chart-container']}>
      <div className={styles['chart-filters']}>
        <ButtonGroup>
          <button
            className={classNames('btn', {
              active: energyType === EnergyType.ELECTRICITY
            })}
            onClick={() => setEnergyType(EnergyType.ELECTRICITY)}
          >
            {t('filterElectricity', 'Elektriciteit')}
          </button>
          <button
            className={classNames('btn', {
              active: energyType === EnergyType.GAS
            })}
            onClick={() => setEnergyType(EnergyType.GAS)}
          >
            {t('filterGas', 'Gas')}
          </button>
        </ButtonGroup>
        <ButtonGroup>
          <button
            className={classNames('btn', {
              active: region === RegionType.FLA
            })}
            onClick={() => setRegion(RegionType.FLA)}
          >
            {t('filterRegionFla', 'VLA')}
          </button>
          <button
            className={classNames('btn', {
              active: region === RegionType.WAL
            })}
            onClick={() => setRegion(RegionType.WAL)}
          >
            {t('filterRegionWal', 'WAL')}
          </button>
          {/* <button
            className={classNames('btn', {
              [styles.active]: region === RegionType.BRU,
            })}
            onClick={ () => setRegion(RegionType.BRU) }>
            { t('filterRegionBru', 'BRU') }
          </button> */}
        </ButtonGroup>
      </div>
      <div className={styles['chart-wrapper']}>
        <HorizontalBar ref={chartRef} data={chartData} height={340} options={options} />
      </div>
      <div className={styles['chart-legend']}>
        <div className={styles.legend}>
          <div className={classNames(styles['color-block'], styles['dark-gray'])} />
          <span>{t('chartLegendBigSuppliers', 'Grote leveranciers')}</span>
        </div>
        <div className={styles.legend}>
          <div className={classNames(styles['color-block'], styles.green)} />
          <span>Bolt</span>
        </div>
      </div>
      <div className={styles['chart-description']}>
        {parse(
          t(
            'chartDescription',
            'Deze data zijn gebaseerd op <a href="#">het jaarlijks rapport van de CREG</a> (Commissie voor de Regulering van Elektriciteit en Gas). De grafiek rangschikt de gemiddelde prijs (voor een verbruik van 3500 kWh) op jaarbasis per leverancier.'
          )
        )}
      </div>
    </div>
  )
}

export default BarChartMobile
